import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BuiltInFieldKey, CommunityProfileField } from '~/interfaces/community';

import type { GeolocationChange } from '../_utils/geolocationField';
import { builtInField } from '../_utils/onboardingProfileFields';

/** Matches the length CommunityMember validates on both columns. */
export const BUILT_IN_FIELD_MAX_LENGTH = 100;

/** The picture is a file on the subscription, so only these two carry a string the hook owns. */
type ValuedBuiltInKey = Exclude<BuiltInFieldKey, 'avatar'>;
type BuiltInValues = Record<ValuedBuiltInKey, string>;
type BuiltInErrors = Partial<Record<ValuedBuiltInKey, string>>;

// The built-in configuration shared by onboarding and Edit Profile: which inputs render, whether
// they are required, and what reaches the request. The hook owns the headline and location values
// so editing one clears its error in a single place.
const useBuiltInProfileFields = (profileFields: CommunityProfileField[], initialValues: BuiltInValues) => {
  const { t } = useTranslation();
  const headlineField = builtInField(profileFields, 'headline');
  const locationField = builtInField(profileFields, 'location');
  const avatarField = builtInField(profileFields, 'avatar');
  const [values, setValues] = useState<BuiltInValues>(initialValues);
  const [errors, setErrors] = useState<BuiltInErrors>({});

  const setValue = (key: ValuedBuiltInKey, value: string) => {
    setValues((previous) => ({ ...previous, [key]: value }));
    if (errors[key]) setErrors((previous) => ({ ...previous, [key]: undefined }));
  };

  const validate = (): boolean => {
    const nextErrors: BuiltInErrors = {};
    if (headlineField.required && !values.headline.trim()) nextErrors.headline = t('community.onboard.required');
    if (locationField.required && !values.location.trim()) nextErrors.location = t('community.onboard.required');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Hidden fields never reach the request; the server rejects writes to them.
  const requestValues = (geolocation?: GeolocationChange) => ({
    headline: headlineField.visible ? values.headline.trim() : undefined,
    location: locationField.visible ? values.location.trim() : undefined,
    geolocation: locationField.visible ? geolocation : undefined,
  });

  return {
    headlineField,
    locationField,
    avatarField,
    headline: values.headline,
    setHeadline: (value: string) => setValue('headline', value),
    location: values.location,
    setLocation: (value: string) => setValue('location', value),
    errors,
    validate,
    requestValues,
  };
};

export default useBuiltInProfileFields;
